'use client';

import React from 'react';
import { TimelineComponent } from '../types/content-components.types.js';
import { ComponentIcon } from './ComponentIcon.js';
import { useComponentAnimation } from './hooks/useComponentAnimation.js';

interface TimelineProps {
  component: TimelineComponent;
}

export const Timeline: React.FC<TimelineProps> = ({ component }) => {
  const { props } = component;
  const { ref, isVisible } = useComponentAnimation(props.animation);

  const getItemColor = (variant?: string) => {
    switch (variant) {
      case 'primary':
        // Muted Sage
        return { dot: 'bg-learn-sage-500', line: 'bg-learn-sage-100', text: 'text-learn-sage-900', icon: 'text-white' };
      case 'success':
        // Growth Green (muted)
        return { dot: 'bg-green-500', line: 'bg-green-100', text: 'text-green-900', icon: 'text-white' };
      case 'warning':
        // Muted Honey
        return { dot: 'bg-learn-honey-500', line: 'bg-learn-honey-100', text: 'text-learn-honey-900', icon: 'text-white' };
      case 'danger':
        return { dot: 'bg-red-500', line: 'bg-red-100', text: 'text-red-900', icon: 'text-white' };
      case 'info':
        // Muted Sky Blue
        return { dot: 'bg-learn-sky-500', line: 'bg-learn-sky-100', text: 'text-learn-sky-900', icon: 'text-white' };
      case 'highlight':
        // Muted Clay/Coral
        return { dot: 'bg-learn-clay-500', line: 'bg-learn-clay-100', text: 'text-learn-clay-900', icon: 'text-white' };
      default:
        // Soft default
        return { dot: 'bg-learn-sage-400', line: 'bg-learn-sage-100', text: 'text-gray-900', icon: 'text-white' };
    }
  };

  return (
    <div ref={ref} className="w-full">
      {props.title && (
        <h3 className="text-xl font-bold text-gray-900 mb-6">{props.title}</h3>
      )}

      <div className="space-y-6">
        {props.items.map((item, index) => {
          const colors = getItemColor(item.variant);
          const isLast = index === props.items.length - 1;
          const animationDelay = props.animation === 'stagger' ? `${index * 150}ms` : '0ms';

          return (
            <div
              key={item.id + '-' + index}
              className={`flex gap-4 ${isVisible ? 'animate-fadeIn' : ''}`}
              style={{ 
                animationDelay,
                opacity: isVisible ? undefined : 0
              }}
            >
              {/* Timeline Track */}
              <div className="flex flex-col items-center flex-shrink-0">
                {/* Dot/Icon */}
                <div className={`w-10 h-10 ${colors.dot} rounded-full flex items-center justify-center shadow-md z-10`}>
                  {item.icon ? (
                    <ComponentIcon icon={item.icon} className={`w-5 h-5 ${colors.icon}`} />
                  ) : (
                    <span className={`text-sm font-bold ${colors.icon}`}>{index + 1}</span>
                  )}
                </div>
                
                {/* Connecting Line */}
                {!isLast && (
                  <div className={`w-0.5 ${colors.line} flex-1 min-h-[40px] mt-2`} />
                )}
              </div>

              {/* Content */}
              <div className="flex-1 pb-6">
                {/* Label/Date */}
                <div className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1">
                  {item.label}
                </div>

                {/* Title */}
                <h4 className={`text-lg font-bold ${colors.text} mb-2`}>
                  {item.title}
                </h4>

                {/* Description */}
                <p className="text-sm text-gray-700 leading-relaxed mb-2">
                  {item.content}
                </p>

                {/* Metadata (optional) */}
                {item.metadata && (
                  <div className="text-xs text-gray-500 italic mt-2">
                    {item.metadata}
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

